import { DocumentNode } from 'graphql';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import CancelOrderMutation from './CancelOrderMutation';
import GetOrderQuery from './GetOrderQuery';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';

type Order = GetOrder['order'];

export interface CancelOrderFormProps {
  order: Order;
  refetchQueries: DocumentNode[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CancelOrderForm = ({
  order, onSuccess, onCancel, refetchQueries = [GetParticipantOrdersQuery, GetOrderQuery],
}: CancelOrderFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [cancelOrder, { loading }] = useMutation(
    CancelOrderMutation,
    {
      variables: {
        id: order.id,
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.order_cancelled'));
      },
    },
  );

  return (
    <UI.Form onSubmit={() => cancelOrder()}>
      <UI.FormGrid>
        <UI.H4>
          {t('cancel_order')}
        </UI.H4>
        <UI.Paragraph>
          {t('cancel_order_description')}
        </UI.Paragraph>
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} disabled={loading}>
              {t('cancel')}
            </UI.Button>
          )}
          {' '}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={loading}
          >
            {t('cancel_order')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};
interface UseCancelOrderModalProps {
  order?: Order;
  refetchQueries?: DocumentNode[];
}

const useCancelOrderModal = ({ order, refetchQueries }: UseCancelOrderModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal}>
        {!order && <UI.PageLoader />}
        {order && (
          <CancelOrderForm
            order={order}
            refetchQueries={refetchQueries}
            onSuccess={closeModal}
            onCancel={closeModal}
          />
        )}
      </UI.Modal>
    ),
    [order],
  );

  return [openModal, closeModal];
};

export default useCancelOrderModal;
